'use client';

import React, { createContext, useState, useContext, useRef, useEffect, startTransition } from 'react';

interface AudioContextType {
  isPlaying: boolean;
  duration: number;
  currentTime: number;
  hasPlayedBefore: boolean;
  isLoading: boolean;
  isVisible: boolean;
  audioRef: React.RefObject<HTMLAudioElement>;
  togglePlayPause: () => void;
  showAudioPlayer: () => void;
  setCurrentTime: (time: number) => void;
  formatTime: (time: number) => string;
  setIsVisible: (value: boolean) => void;
  playbackRate: number;
  setPlaybackRate: (rate: number) => void;
}

const AudioContext = createContext<AudioContextType | undefined>(undefined);

const POSITION_STORAGE_KEY = 'audioPlayerLastPosition-new';
const VISIBLE_STORAGE_KEY = 'audioPlayerVisible';
// Playhead positions within the first few seconds are treated as "start over":
// they're cleared rather than saved, so a reload starts at exactly 0:00 instead
// of an almost-but-not-quite-zero position.
const MIN_RESTORE_SECONDS = 5;

// Persist the playhead so a returning reader picks up where they left off.
const savePosition = (current: number, duration: number) => {
  if (!isNaN(duration) && duration > 0 && current >= MIN_RESTORE_SECONDS && current < duration) {
    localStorage.setItem(POSITION_STORAGE_KEY, String(current));
  } else {
    localStorage.removeItem(POSITION_STORAGE_KEY);
  }
};

export function AudioProvider({ children }: { children: React.ReactNode }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [hasPlayedBefore, setHasPlayedBefore] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isClient, setIsClient] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  // Flips on the first explicit audio interaction (play toggle / opening the
  // player). The audio element is NOT created at page load: `new Audio` +
  // `.load()` runs the 62MB voiceover through the browser's media pipeline on
  // every visit, and a failure there kills the whole page in WebKit (reliable
  // page crash on iOS 27 beta). Until this is true the site never touches the
  // media stack; readers who never use audio never pay for it.
  const [audioRequested, setAudioRequested] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Handle client-side only rendering. Restoring `isVisible` here (rather than
  // in the useState initializer) keeps the server and first client render in
  // agreement, avoiding a hydration mismatch.
  // As a transition: this provider wraps every page, so a plain (urgent)
  // update here fires right after the layout hydrates and reaches any
  // Suspense boundary below that hasn't hydrated yet. If that boundary's JS
  // is still loading, React gives up hydrating it and client-renders from
  // scratch, blanking the page meanwhile. Transitions let React wait instead.
  useEffect(() => {
    startTransition(() => {
      setIsVisible(localStorage.getItem(VISIBLE_STORAGE_KEY) === 'true');
      setIsClient(true);
    });
  }, []);

  // Persist the open/closed toggle so the player comes back the way the
  // reader left it. Skipped until `isClient` so the restore above runs first.
  useEffect(() => {
    if (!isClient) return;
    localStorage.setItem(VISIBLE_STORAGE_KEY, String(isVisible));
  }, [isVisible, isClient]);

  // An open widget counts as an audio request: fetch the metadata as soon as
  // the UI is visible (including restored-open from localStorage) so the
  // scrub bar has a duration to render immediately, instead of only appearing
  // after the first play. One-way latch — closing the widget doesn't tear the
  // element down. Readers who never open the UI still never touch the media
  // stack (the WebKit-crash concern above).
  useEffect(() => {
    if (isVisible) setAudioRequested(true);
  }, [isVisible]);

  // Note: once the reader opens the audio UI it stays available for the rest
  // of the scroll (the HUD's bottom narration bar depends on `isVisible`
  // staying true). We deliberately do NOT auto-hide it on scroll depth; an
  // earlier version force-hid it past scrollY > 1000 when not playing, which
  // made the HUD bar appear on scroll and then vanish a moment later.

  // Create and set up the audio element, lazily on first interaction (see
  // audioRequested above). Declared before the playback effect below so that
  // when a single tap both requests audio and starts playback, this effect
  // creates the element first in the same commit.
  useEffect(() => {
    if (!isClient || !audioRequested) return;
    // ?noaudio skips the audio element even on interaction (every consumer
    // already null-guards audioRef.current). Kill switch for bisecting page
    // crashes in the browser's media pipeline on device farms — same
    // escape-hatch pattern as the graph's ?dither2d.
    if (window.location.search.includes('noaudio')) return;

    // Create audio element
    const audio = new Audio('/audio/ai-2040-vo-edited.mp3');
    // Fetch only metadata up front (duration, for the widget); the full file
    // downloads lazily when the user first hits play.
    audio.preload = 'metadata';
    audioRef.current = audio;

    // Set up event listeners
    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
      setIsLoading(false);
      // Restore the saved position, if it's meaningfully into the audio.
      // Anything below MIN_RESTORE_SECONDS (or otherwise invalid) is cleared
      // so playback starts at exactly 0.
      const savedTime = localStorage.getItem(POSITION_STORAGE_KEY);
      if (savedTime) {
        const numericTime = parseFloat(savedTime);
        if (!isNaN(numericTime) && numericTime >= MIN_RESTORE_SECONDS && numericTime < audio.duration) {
          audio.currentTime = numericTime;
          // Update state immediately after setting audio time
          setCurrentTime(numericTime);
        } else {
          localStorage.removeItem(POSITION_STORAGE_KEY);
        }
      }
    };

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    // The 2s interval below can be up to 2s stale, so also save on pause
    // (which fires on explicit pause and on unmount).
    const handlePause = () => {
      savePosition(audio.currentTime, audio.duration);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
      // Reset saved position when audio finishes
      localStorage.removeItem(POSITION_STORAGE_KEY);
    };

    // Add event listeners
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('ended', handleEnded);

    // Force load the audio
    audio.load();

    return () => {
      // Clean up event listeners
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('ended', handleEnded);
      audio.pause(); // Ensure audio stops on unmount
    };
  }, [isClient, audioRequested]);

  // Control audio playback when isPlaying changes
  useEffect(() => {
    if (!audioRef.current || !isClient) return;

    if (isPlaying) {
      const playPromise = audioRef.current.play();
      
      // Handle the play promise to avoid "play() failed because the user didn't interact with the document first" errors
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            // Playback started successfully
            if (!hasPlayedBefore) {
              setHasPlayedBefore(true);
            }
          })
          .catch(error => {
            // Auto-play was prevented
            setIsPlaying(false);
            console.error("Playback was prevented:", error);
          });
      }
    } else {
      audioRef.current.pause();
    }
  }, [isPlaying, hasPlayedBefore, isClient]); // Added isClient dependency

  // Update playback rate when it changes
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = playbackRate;
    }
  }, [playbackRate]);

  // Periodically save position while playing
  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;

    if (isPlaying && isClient && audioRef.current) {
      intervalId = setInterval(() => {
        if (audioRef.current) {
          savePosition(audioRef.current.currentTime, audioRef.current.duration);
        }
      }, 2000); // Save every 2 seconds
    }

    // Cleanup function to clear the interval
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isPlaying, isClient]); // Re-run this effect when isPlaying or isClient changes

  const togglePlayPause = () => {
    setAudioRequested(true);
    setIsPlaying(prev => !prev);
    if (!isVisible) {
      setIsVisible(true);
    }
  };

  const showAudioPlayer = () => {
    setAudioRequested(true);
    setIsVisible(true);
  };

  // Format time in mm:ss
  const formatTime = (time: number) => {
    if (isNaN(time) || time === 0) return '0:00';
    
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? '0' + seconds : seconds}`;
  };

  // Set current time directly (for seeking). Saves immediately so a seek
  // while paused still persists (the interval above only runs during playback).
  const handleSetCurrentTime = (time: number) => {
    if (audioRef.current) {
      audioRef.current.currentTime = time;
      setCurrentTime(time);
      savePosition(time, audioRef.current.duration);
    }
  };

  return (
    <AudioContext.Provider value={{ 
      isPlaying, 
      duration, 
      currentTime,
      hasPlayedBefore,
      isLoading,
      isVisible,
      audioRef,
      togglePlayPause, 
      showAudioPlayer,
      setCurrentTime: handleSetCurrentTime,
      formatTime,
      setIsVisible,
      playbackRate,
      setPlaybackRate
    }}>
      {children}
    </AudioContext.Provider>
  );
}

export function useAudio() {
  const context = useContext(AudioContext);
  if (context === undefined) {
    throw new Error('useAudio must be used within an AudioProvider');
  }
  return context;
} 